import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Award,
  Check,
  ChevronDown,
  FileText,
  Globe,
  Heart,
  PlayCircle,
  ShieldCheck,
  Star,
  StarHalf,
  X,
} from "lucide-react";
import CourseNavbar from "../components/landing/CourseNavbar";
import CartToast from "../components/landing/CartToast";
import { useLandingCartSync } from "../components/landing/useLandingCart";
import { useWishlist } from "../components/landing/useWishlist";
import { formatNaira } from "../components/landing/landingData";
import {
  formatCount,
  formatDuration,
  mapPublicCourse,
  slugify,
  UUID_RE,
} from "../components/landing/publicCourseMap";
import { shortDate } from "../lib/format";
import { useUserPublicCatalog } from "../store/user";
import LearningImg from "../assets/learning.jpeg";
import authSession from "../lib/authSession";
import {useUserAuth} from "../store/user";

/** Render a rich-HTML field returned by the catalog API. */
function ApiHtml({ html, className = "" }) {
  if (!html) return null;
  return (
    <div
      className={`min-w-0 break-words font-inter text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere] [&_a]:break-words [&_img]:h-auto [&_img]:max-w-full [&_li]:leading-relaxed [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_pre]:overflow-x-auto [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5 ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function LectureTypeIcon({ type }) {
  if (String(type).toUpperCase() === "VIDEO") {
    return <PlayCircle className="h-4 w-4 flex-shrink-0 text-slate-400" />;
  }
  return <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />;
}

/** Curriculum accordion — driven entirely by the API's `curriculum.sections`. */
/** Curriculum accordion — driven by the API's `stats` + `curriculum.sections`. */
function CourseContent({ sections, stats, onPreview }) {
  const orderedSections = useMemo(
    () => [...sections].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)),
    [sections]
  );
  const [open, setOpen] = useState(() =>
    new Set(orderedSections[0] ? [orderedSections[0].id] : [])
  );

  const toggle = (id) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Header numbers come from `stats` (the same source as the hero + includes),
  // falling back to the curriculum array when a payload omits them.
  const sectionCount =
    stats && stats.sectionCount !== undefined
      ? Number(stats.sectionCount) || 0
      : orderedSections.length;
  const lectureCount =
    stats && stats.lectureCount !== undefined
      ? Number(stats.lectureCount) || 0
      : orderedSections.reduce((sum, section) => sum + (section.lectures?.length || 0), 0);
  const durationSeconds =
    stats && stats.totalDurationSeconds !== undefined
      ? Number(stats.totalDurationSeconds) || 0
      : orderedSections.reduce((sum, section) => sum + (Number(section.totalDurationSeconds) || 0), 0);

  if (!sectionCount) {
    return (
      <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-2 text-2xl font-bold">Course content</h2>
        <p className="mb-4 text-sm text-slate-500">
          {formatCount(sectionCount)} sections • {formatCount(lectureCount)} lectures •{" "}
          {formatDuration(durationSeconds) || "0 minutes"} total length
        </p>
        <p className="text-sm text-slate-500">The curriculum will be available soon.</p>
      </div>
    );
  }

  return (
    <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
      <h2 className="mb-2 text-2xl font-bold">Course content</h2>
      <p className="mb-4 text-sm text-slate-500">
        {formatCount(sectionCount)} section{sectionCount === 1 ? "" : "s"} •{" "}
        {formatCount(lectureCount)} lecture{lectureCount === 1 ? "" : "s"} •{" "}
        {formatDuration(durationSeconds) || "0 minutes"} total length
      </p>
      <div className="divide-y divide-slate-200 border-y border-slate-200">
        {orderedSections.map((section) => {
          const isOpen = open.has(section.id);
          const sectionDuration = formatDuration(section.totalDurationSeconds);
          const lectures = [...(section.lectures || [])].sort(
            (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)
          );
          const sectionLectures =
            section.lectureCount !== undefined
              ? Number(section.lectureCount) || 0
              : lectures.length;
          return (
            <div key={section.id}>
              <button
                type="button"
                onClick={() => toggle(section.id)}
                className="flex w-full items-center justify-between gap-4 py-3 text-left"
              >
                <span className="min-w-0 break-words text-sm font-semibold text-slate-800">
                  {section.title}
                </span>
                <span className="flex flex-shrink-0 items-center gap-3 text-xs text-slate-500">
                  {formatCount(sectionLectures)} lectures
                  {` • ${sectionDuration || "0 minutes"}`}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </span>
              </button>
              {isOpen && (
                <ul className="pb-3">
                  {lectures.map((lecture) => (
                    <li
                      key={lecture.id}
                      className="flex items-center justify-between gap-4 py-2 pl-2 text-sm text-slate-600"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <LectureTypeIcon type={lecture.type} />
                        <span className="min-w-0 break-words">{lecture.title}</span>
                        {lecture.isPreview && (
                          <button
                            type="button"
                            onClick={() => onPreview(lecture)}
                            className="flex-shrink-0 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                          >
                            Preview
                          </button>
                        )}
                      </span>
                      <span className="flex-shrink-0 text-xs text-slate-400">
                        {formatDuration(lecture.durationSeconds) || "0 minutes"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StarRating({ rating }) {
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <span key={i}>
          {i < full ? (
            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          ) : i === full && hasHalf ? (
            <StarHalf className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          ) : (
            <Star className="h-4 w-4 text-yellow-400" />
          )}
        </span>
      ))}
    </div>
  );
}

/** Layout-matched placeholder shown while the course detail loads. */
function CourseDetailSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <CourseNavbar />

      {/* Hero */}
      <section className="bg-slate-900 pb-6 text-white lg:min-h-[300px]">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6">
          <div className="h-4 w-16 animate-pulse rounded bg-white/20" />
          <div className="mt-6 max-w-3xl space-y-3">
            <div className="h-8 w-3/4 animate-pulse rounded bg-white/20" />
            <div className="h-4 w-full animate-pulse rounded bg-white/10" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-white/10" />
            <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2">
              <div className="h-4 w-28 animate-pulse rounded bg-white/10" />
              <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
              <div className="h-4 w-40 animate-pulse rounded bg-white/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="min-w-0 space-y-8 pt-8 lg:col-span-2">
            {[0, 1, 2].map((block) => (
              <div key={block} className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="mb-4 h-6 w-48 animate-pulse rounded bg-slate-200" />
                <div className="space-y-2">
                  <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                  <div className="h-4 w-11/12 animate-pulse rounded bg-slate-100" />
                  <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>

          <div className="min-w-0 lg:col-span-1 lg:-mt-56">
            <div className="overflow-hidden rounded-lg bg-white shadow-2xl">
              <div className="h-48 w-full animate-pulse bg-slate-200" />
              <div className="space-y-3 p-6">
                <div className="h-8 w-32 animate-pulse rounded bg-slate-200" />
                <div className="h-12 w-full animate-pulse rounded bg-slate-200" />
                <div className="h-12 w-full animate-pulse rounded bg-slate-100" />
                <div className="mx-auto h-4 w-2/3 animate-pulse rounded bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Modal that plays a free preview lecture (VIDEO embed or article content). */
function PreviewModal({ lesson, data, loading, error, onClose }) {
  if (!lesson) return null;
  const video = data?.videos?.[0];
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${lesson.title}`}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <h2 className="min-w-0 break-words text-lg font-bold text-slate-900">{lesson.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          {loading ? (
            <div className="aspect-video w-full animate-pulse rounded bg-slate-200" />
          ) : error ? (
            <p className="py-10 text-center text-sm text-red-600">{error}</p>
          ) : video?.videoEmbedUrl ? (
            <div className="aspect-video w-full overflow-hidden rounded bg-black">
              <iframe
                src={video.videoEmbedUrl}
                title={video.title || lesson.title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : data?.content ? (
            <ApiHtml html={data.content} />
          ) : (
            <p className="py-10 text-center text-sm text-slate-500">
              This preview has no playable content yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CourseDetailPage() {
  const navigate = useNavigate();
  const { id: routeParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {userIsAuthenticated} = useUserAuth();
  const authSnapshot = useSyncExternalStore(authSession.subscribe, authSession.getSnapshot);
  const authenticated = userIsAuthenticated || (authSnapshot.status === "authenticated" && authSnapshot.role === "user");
  const { isInCart, addCourse } = useLandingCartSync();
  const { has: hasWishlisted, toggle: toggleWishlist } = useWishlist();
  const {
    publicCourse,
    publicCourseLoading,
    publicCourseError,
    fetchPublicCourse,
    fetchPublicSearch,
    fetchPublicCourseReviews,
    fetchPublicLessonPreview,
  } = useUserPublicCatalog();

  // Course URLs use the slug. The deployed API still returns `slug: null` and
  // 404s on slug detail/review paths, so resolve the slug to an id through
  // public search first; fall back to the slug directly for newer backends
  // (the contract allows either UUID or slug on the detail endpoint).
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!routeParam) return undefined;
    let active = true;
    setResolving(true);

    const load = async () => {
      try {
        if (UUID_RE.test(routeParam)) {
          await fetchPublicCourse(routeParam);
          return;
        }

        const query = routeParam.replace(/-/g, " ").trim();
        const result = await fetchPublicSearch({ q: query, limit: 20 });
        if (!active) return;

        const match = (result?.items || []).find(
          (item) =>
            String(item?.type).toUpperCase() === "COURSE" &&
            (item?.slug === routeParam || slugify(item?.title) === routeParam)
        );

        if (match?.id) {
          await fetchPublicCourse(match.id);
          return;
        }

        // Slug-capable backend: ask for it directly.
        await fetchPublicCourse(routeParam);
      } finally {
        if (active) setResolving(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [authenticated, routeParam, fetchPublicCourse, fetchPublicSearch]);

  const course = useMemo(() => {
    if (!publicCourse) return null;
    const mapped = mapPublicCourse(publicCourse);
    // Ignore a stale course left over from a previous page while the new one loads.
    const matchesParam = UUID_RE.test(routeParam)
      ? publicCourse.id === routeParam
      : mapped.slug === routeParam;
    if (!matchesParam) return null;
    return {
      ...mapped,
      type: "course",
      image: mapped.image || LearningImg,
      thumbnail: mapped.image || LearningImg,
    };
  }, [publicCourse, routeParam]);

  // Nested endpoints (reviews, preview) are keyed by the course id — the slug
  // is only a frontend URL concern.
  const courseKey = course ? course.id : "";

  // ── Public reviews ─────────────────────────────────────────────────────────
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsPages, setReviewsPages] = useState(1);
  const [reviewsMoreLoading, setReviewsMoreLoading] = useState(false);

  useEffect(() => {
    if (!courseKey) return undefined;
    let active = true;
    setReviewsLoading(true);
    setReviewsError("");
    setReviews([]);
    setReviewsPage(1);
    setReviewsPages(1);

    fetchPublicCourseReviews(courseKey, { page: 1, limit: 6, sort: "newest" }).then((result) => {
      if (!active) return;
      setReviewsLoading(false);
      if (result.success) {
        setReviews(result.items || []);
        setReviewsPages(result.totalPages || 1);
      } else {
        setReviewsError(result.error || "Could not load reviews.");
      }
    });

    return () => {
      active = false;
    };
  }, [courseKey, fetchPublicCourseReviews]);

  const loadMoreReviews = async () => {
    if (!courseKey || reviewsMoreLoading) return;
    const nextPage = reviewsPage + 1;
    setReviewsMoreLoading(true);
    const result = await fetchPublicCourseReviews(courseKey, {
      page: nextPage,
      limit: 6,
      sort: "newest",
    });
    setReviewsMoreLoading(false);
    if (result.success) {
      setReviews((prev) => [...prev, ...(result.items || [])]);
      setReviewsPage(nextPage);
      setReviewsPages(result.totalPages || nextPage);
    }
  };

  // ── Preview lecture ────────────────────────────────────────────────────────
  const [previewLesson, setPreviewLesson] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [studyMessage, setStudyMessage] = useState("");

  const openPreview = useCallback(async (lesson) => {
    if (!course) return;
    if (course.isFree && !authenticated) {
      const returnTo = `/courses/${encodeURIComponent(routeParam)}?study=1`;
      navigate(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    setPreviewLesson(lesson);
    setPreviewData(null);
    setPreviewError("");
    setPreviewLoading(true);
    const result = await fetchPublicLessonPreview(course.id, lesson.id);
    setPreviewLoading(false);
    if (result.success) setPreviewData(result.data);
    else setPreviewError(result.error || "Could not load this preview lesson.");
  }, [authenticated, course, fetchPublicLessonPreview, navigate, routeParam]);

  const closePreview = () => {
    setPreviewLesson(null);
    setPreviewData(null);
    setPreviewError("");
  };

  const firstPreviewLesson = course?.curriculum?.sections
    ?.flatMap((section) => section.lectures || [])
    .find((lesson) => lesson.isPreview);

  useEffect(() => {
    if (!course || !authenticated || searchParams.get("study") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("study");
    setSearchParams(next, {replace: true});
    if (course.viewer.isEnrolled) navigate(`/courses/${course.id}/read`);
    else if (firstPreviewLesson) openPreview(firstPreviewLesson);
  }, [authenticated, course, firstPreviewLesson, navigate, openPreview, searchParams, setSearchParams]);

  if ((publicCourseLoading || resolving) && !course) {
    return <CourseDetailSkeleton />;
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-white">
        <CourseNavbar />
        <div className="mx-auto flex max-w-xl flex-col items-center gap-3 px-6 py-24 text-center">
          <AlertCircle className="h-10 w-10 text-red-500" />
          <h1 className="text-xl font-bold text-slate-900">Course unavailable</h1>
          <p className="text-sm text-slate-600">
            {publicCourseError || "We couldn't load this course. It may have been removed."}
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-2 inline-flex items-center gap-2 rounded-md bg-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  const inCart = isInCart(course.id) || course.viewer.isInCart;
  const wished = hasWishlisted(course.id) || course.viewer.isWishlisted;
  const enrolled = course.viewer.isEnrolled;
  const stats = course.stats;
  const durationDetailed = formatDuration(stats.totalDurationSeconds);
  const sections = course.curriculum.sections;
  const learningItems = course.learning.items;
  const resourcePersons = course.resourcePersons;
  const instructorNames = resourcePersons.map((person) => person?.name).filter(Boolean).join(", ");

  // Includes always list the duration/lecture slots (0 when the API has no
  // timing yet) so the card fills in automatically once data is available.
  const includes = [
    { icon: PlayCircle, text: `${durationDetailed || "0 minutes"} of on-demand video` },
    {
      icon: FileText,
      text: `${formatCount(stats.lectureCount)} lecture${stats.lectureCount === 1 ? "" : "s"}`,
    },
  ];
  if (course.includes.hasCertificate) {
    includes.push({ icon: Award, text: "Certificate of completion" });
  }
  if (course.includes.accessType) {
    includes.push({
      icon: Globe,
      text: course.includes.accessType === "LIMITED" ? "Limited access" : "Full lifetime access",
    });
  }
  if (course.includes.moneyBackDays > 0) {
    includes.push({
      icon: ShieldCheck,
      text: `${course.includes.moneyBackDays}-day money-back guarantee`,
    });
  }

  const handleAddToCart = () => addCourse(course);
  const handleStartFreeStudy = () => {
    if (!authenticated) {
      const returnTo = `/courses/${encodeURIComponent(routeParam)}?study=1`;
      navigate(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    if (enrolled) {
      navigate(`/courses/${course.id}/read`);
      return;
    }
    if (firstPreviewLesson) {
      setStudyMessage("");
      openPreview(firstPreviewLesson);
      return;
    }
    setStudyMessage("This course does not have a study preview available yet.");
  };
  const handleBuyNow = () => {
    addCourse(course);
    navigate(`/cart?checkoutId=${encodeURIComponent(course.id)}&checkoutType=course`);
  };

  return (
    <div className="min-h-screen bg-white">
      <CartToast />
      <CourseNavbar />

      {/* Dark hero band — grows with its content so nothing is clipped on mobile */}
      <section className="bg-slate-900 pb-6 text-white lg:min-h-[300px]">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="mt-4 max-w-3xl">
            {(course.badges.bestseller || course.badges.hot) && (
              <div className="mb-2 flex flex-wrap gap-2">
                {course.badges.bestseller && (
                  <span className="rounded-sm bg-white px-2 py-0.5 text-xs font-semibold text-blue-700">
                    Bestseller
                  </span>
                )}
                {course.badges.hot && (
                  <span className="rounded-sm bg-white px-2 py-0.5 text-xs font-semibold text-red-600">
                    Hot
                  </span>
                )}
              </div>
            )}
            <h1 className="mb-3 break-words text-2xl font-bold md:text-4xl">{course.title}</h1>
            <p className="mb-4 break-words font-nunito text-sm text-slate-300">
              {course.summary || course.description}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-bold text-yellow-400">
                  {course.ratingAverage === null ? "—" : course.ratingAverage.toFixed(1)}
                </span>
                <StarRating rating={course.ratingAverage ?? 0} />
                <span className="text-slate-300">
                  ({formatCount(course.reviewCount)} review
                  {course.reviewCount === 1 ? "" : "s"})
                </span>
              </div>
              <span className="text-slate-300">
                {formatCount(course.studentCount)} student
                {course.studentCount === 1 ? "" : "s"}
              </span>
              <span className="text-slate-300">
                {durationDetailed || "0 minutes"} • {formatCount(stats.lectureCount)} lectures
                {course.level ? ` • ${course.level}` : ""}
              </span>
            </div>
            <div className="mt-3 break-words text-sm text-slate-300">
              Created by{" "}
              <span className="text-blue-300">
                {instructorNames || "Instructor to be announced"}
              </span>
            </div>
            {course.updatedAt && (
              <div className="mt-1 text-xs text-slate-400">
                Last updated {shortDate(course.updatedAt)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main content — the purchase card is sticky as the page scrolls */}
      <div className="mx-auto max-w-7xl break-words px-4 pb-16 sm:px-6">
        <div className="grid min-w-0 grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="min-w-0 pt-8 lg:col-span-2">
            <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="mb-4 text-2xl font-bold">What you'll learn</h2>
              {learningItems.length > 0 ? (
                <>
                  {course.learning.intro && (
                    <p className="mb-4 text-sm text-slate-600">{course.learning.intro}</p>
                  )}
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {learningItems.map((text, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
                        <span className="text-sm text-slate-700">{text}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : course.learningOutcomesHtml ? (
                <ApiHtml html={course.learningOutcomesHtml} />
              ) : (
                <p className="text-sm text-slate-500">
                  Learning outcomes will be available soon.
                </p>
              )}

              <div className="mt-6 border-t border-slate-100 pt-5">
                <h3 className="mb-3 text-sm font-bold text-slate-900">Explore related topics</h3>
                {course.topics.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {course.topics.map((topic) => (
                      <span
                        key={topic.slug || topic.name}
                        className="max-w-full break-words rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
                      >
                        {topic.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Topics will be available soon.</p>
                )}
              </div>
            </div>

            <CourseContent key={course.id} sections={sections} stats={stats} onPreview={openPreview} />

            <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
              <h2 className="mb-4 text-2xl font-bold">Requirements</h2>
              {course.requirementsHtml ? (
                <ApiHtml html={course.requirementsHtml} />
              ) : (
                <p className="text-sm text-slate-500">Requirements will be available soon.</p>
              )}
            </div>

            <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6 font-nunito">
              <h2 className="mb-4 text-2xl font-bold">Description</h2>

              {course.introductionHtml ? (
                <ApiHtml html={course.introductionHtml} className="mb-5 font-nunito" />
              ) : null}
              {course.descriptionHtml ? (
                <ApiHtml html={course.descriptionHtml} className="mb-5 font-nunito" />
              ) : null}
              {!course.descriptionHtml && !course.introductionHtml && (
                <p className="mb-5 text-sm text-slate-500">
                  A full description will be available soon.
                </p>
              )}

              <h4 className="mb-3 text-sm font-bold text-slate-900">
                {resourcePersons.length > 1 ? "About the instructors" : "About the instructor"}
              </h4>
              {resourcePersons.length > 0 ? (
                <div className="space-y-5">
                  {resourcePersons.map((person) => (
                    <div key={person.id || person.name} className="flex items-start gap-3">
                      {person.avatarUrl ? (
                        <img
                          src={person.avatarUrl}
                          alt={person.name || "Instructor"}
                          className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500">
                          {(person.name || "?")
                            .split(/\s+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((part) => part[0]?.toUpperCase())
                            .join("")}
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{person.name}</p>
                        {person.bio ? (
                          <p className="mt-1 text-sm leading-relaxed text-slate-700">
                            {person.bio}
                          </p>
                        ) : (
                          <p className="mt-1 text-sm text-slate-500">
                            A bio will be available soon.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Instructor details will be available soon.
                </p>
              )}

              {course.targetAudienceHtml && (
                <>
                  <h4 className="mb-2 mt-6 text-sm font-bold text-slate-900">
                    Who this course is for
                  </h4>
                  <ApiHtml html={course.targetAudienceHtml} />
                </>
              )}
            </div>

            {/* Public reviews */}
            <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold">Reviews</h2>
                <span className="flex items-center gap-2 text-sm text-slate-600">
                  <span className="font-bold text-amber-600">
                    {course.ratingAverage === null ? "—" : course.ratingAverage.toFixed(1)}
                  </span>
                  <StarRating rating={course.ratingAverage ?? 0} />
                  <span>
                    ({formatCount(course.reviewCount)} review
                    {course.reviewCount === 1 ? "" : "s"})
                  </span>
                </span>
              </div>

              {reviewsLoading ? (
                <div className="space-y-3">
                  {[0, 1].map((i) => (
                    <div key={i} className="h-20 animate-pulse rounded bg-slate-100" />
                  ))}
                </div>
              ) : reviewsError ? (
                <p className="text-sm text-red-600">{reviewsError}</p>
              ) : reviews.length === 0 ? (
                <p className="text-sm text-slate-500">
                  This course has no reviews yet. Enrolled learners can be the first to review it.
                </p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="rounded-md border border-slate-100 bg-slate-50/60 p-4"
                    >
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">
                          {review.learnerName || "Learner"}
                        </span>
                        <span className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3.5 w-3.5 ${
                                i < review.rating
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-slate-300"
                              }`}
                            />
                          ))}
                        </span>
                        <span className="text-xs text-slate-400">
                          {shortDate(review.createdAt)}
                        </span>
                      </div>
                      {review.comment && (
                        <p className="text-sm leading-relaxed text-slate-700">{review.comment}</p>
                      )}
                    </div>
                  ))}
                  {reviewsPage < reviewsPages && (
                    <button
                      type="button"
                      onClick={loadMoreReviews}
                      disabled={reviewsMoreLoading}
                      className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {reviewsMoreLoading ? "Loading…" : "Show more reviews"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {includes.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <h2 className="mb-4 text-2xl font-bold">This course includes</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {includes.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.text} className="flex items-center gap-3 text-sm text-slate-700">
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Sticky purchase card */}
          <div className="min-w-0 lg:col-span-1 lg:-mt-56">
            <div className="sticky top-20 overflow-hidden rounded-lg bg-white shadow-2xl">
              <img src={course.image} alt={course.title} className="aspect-[16/10] w-full object-cover object-top" />
              <div className="p-6">
                <div className="mb-4 flex flex-wrap items-baseline gap-3">
                  <span className="text-3xl font-bold text-slate-900">
                    {course.isFree ? "Free" : formatNaira(course.price)}
                  </span>
                  {course.discountPercentage > 0 && (
                    <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      {course.discountPercentage}% off
                    </span>
                  )}
                  {course.oldPrice > course.price && (
                    <span className="text-lg text-slate-500 line-through">
                      {formatNaira(course.oldPrice)}
                    </span>
                  )}
                </div>

                  {enrolled ? (
                  <button
                    onClick={() => navigate(course.isFree ? `/courses/${course.id}/read` : "/my-courses")}
                    className="mb-3 flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 py-3 font-semibold text-white transition-colors hover:bg-blue-800"
                  >
                    {course.isFree ? "Continue studying" : "Go to my courses"}
                  </button>
                ) : course.isFree ? (
                  <>
                    <button
                      type="button"
                      onClick={handleStartFreeStudy}
                      className="mb-3 flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 py-3 font-semibold text-white transition-colors hover:bg-blue-800"
                    >
                      Start studying
                    </button>
                    {studyMessage ? <p role="status" className="mb-3 text-xs text-slate-600">{studyMessage}</p> : null}
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleAddToCart}
                      disabled={inCart}
                      className={`mb-3 flex w-full items-center justify-center gap-2 rounded-md py-3 font-semibold transition-colors ${
                        inCart
                          ? "cursor-default border border-blue-200 bg-blue-50 text-blue-800"
                          : "bg-blue-900 text-white hover:bg-blue-800"
                      }`}
                    >
                      {inCart ? (
                        <>
                          <Check className="h-4 w-4" />
                          In cart
                        </>
                      ) : (
                        "Add to cart"
                      )}
                    </button>
                    <button
                      onClick={handleBuyNow}
                      className="w-full rounded-md border border-slate-900 py-3 font-semibold text-slate-900 transition-colors hover:bg-slate-50"
                    >
                      Buy now
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => toggleWishlist(course.id)}
                  aria-pressed={wished}
                  className={`mt-3 flex w-full items-center justify-center gap-2 rounded-md border py-2.5 text-sm font-semibold transition-colors ${
                    wished
                      ? "border-rose-200 bg-rose-50 text-rose-600"
                      : "border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Heart className="h-4 w-4" style={{ fill: wished ? "#f43f5e" : "none" }} />
                  {wished ? "Wishlisted" : "Add to wishlist"}
                </button>

                {course.includes.moneyBackDays > 0 && (
                  <p className="mt-3 text-center text-xs text-slate-600">
                    {course.includes.moneyBackDays}-Day Money-Back Guarantee
                  </p>
                )}

                {includes.length > 0 && (
                  <div className="mt-6 space-y-3">
                    {includes.map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.text}
                          className="flex items-center gap-3 text-sm text-slate-700"
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span>{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PreviewModal
        lesson={previewLesson}
        data={previewData}
        loading={previewLoading}
        error={previewError}
        onClose={closePreview}
      />
    </div>
  );
}
